/**
 * Minispiele im Zonenchat (amendment 19): Tic-Tac-Toe and Schere, Stein, Papier for two, and the
 * magische Miesmuschel for anybody with a question.
 *
 * A game for two starts as an invitation in a district's chat, and whoever takes it first
 * plays. The one who invited is the **host**, the one who took it the **guest**; everybody else
 * in the chat watches. Three rules carry the design:
 *
 * **The server holds the game.** Every move is checked there, and a hand chosen in Schere,
 * Stein, Papier stays there until the other player has chosen too — a client that could see it
 * could also play against it.
 *
 * **Nothing waits forever.** An invitation nobody took lapses after ten minutes, and a game in
 * which nobody moved for ten minutes has fallen asleep. Both are read off the clock whenever
 * somebody looks; no sweep has to run for a forgotten game to stop.
 *
 * **The dice are the server's.** The Miesmuschel's answer is drawn there and stored with the
 * question, so everybody in the chat reads the same one and the asker cannot draw again.
 *
 * Pure like the rest of the directory: the rules, what a move may be, who has won, and what each
 * person may see of a game. The clock and the dice come from the caller.
 */

import type { PartyGame } from './party.ts';

export const DUELS = ['tictactoe', 'rps'] as const;
export type Duel = (typeof DUELS)[number];

/**
 * What a chat message is: words, a question to the Miesmuschel, an invitation to a game for
 * two, or a round of one of the drinking games for everybody (amendment 20).
 */
export type MessageKind = 'text' | 'conch' | Duel | PartyGame;

export function isDuel(value: unknown): value is Duel {
  return DUELS.some((duel) => duel === value);
}

export const DUEL_NAMES: Record<Duel, string> = {
  tictactoe: 'Tic-Tac-Toe',
  rps: 'Schere, Stein, Papier',
};

/**
 * What an invitation says as a message: the line a push shows, and what an app from before the
 * games draws in place of the board.
 */
export const INVITATIONS: Record<Duel, string> = {
  tictactoe: '❌⭕ Tic-Tac-Toe – wer spielt mit?',
  rps: '✊✋✌️ Schere, Stein, Papier – wer spielt mit?',
};

/** An invitation nobody took lapses after this. */
export const INVITE_TTL_MS = 10 * 60 * 1000;

/** A game in which nobody moved for this long has fallen asleep: no winner, no more moves. */
export const IDLE_TTL_MS = 10 * 60 * 1000;

/** Schere, Stein, Papier goes to whoever wins this many rounds first. A tie is played again. */
export const RPS_WINS = 2;

export type Seat = 'host' | 'guest';

const otherSeat = (seat: Seat): Seat => (seat === 'host' ? 'guest' : 'host');

// ---------------------------------------------------------------------------------------
// Tic-Tac-Toe
// ---------------------------------------------------------------------------------------

/**
 * Nine squares, row by row: `.` empty, `x` the guest's, `o` the host's.
 *
 * The guest starts. Of the two, they are the one who is certainly there when the game begins —
 * they have just tapped "Mitspielen" — while the host may have put the phone away after
 * inviting.
 */
export type TicTacToe = { kind: 'tictactoe'; board: string; resigned?: Seat };

const EMPTY_BOARD = '.........';

const LINES = [
  [0, 1, 2],
  [3, 4, 5],
  [6, 7, 8],
  [0, 3, 6],
  [1, 4, 7],
  [2, 5, 8],
  [0, 4, 8],
  [2, 4, 6],
] as const;

const MARKS: Record<Seat, string> = { guest: 'x', host: 'o' };

function count(board: string, mark: string): number {
  return [...board].filter((square) => square === mark).length;
}

/** Whose move it is: the guest's whenever both have made as many. */
function turnOf(board: string): Seat {
  return count(board, MARKS.guest) === count(board, MARKS.host) ? 'guest' : 'host';
}

/** The three squares in a row, once somebody has them. */
export function winningLine(board: string): readonly [number, number, number] | null {
  for (const line of LINES) {
    const mark = board[line[0]];
    if (mark !== '.' && mark === board[line[1]] && mark === board[line[2]]) return line;
  }
  return null;
}

// ---------------------------------------------------------------------------------------
// Schere, Stein, Papier
// ---------------------------------------------------------------------------------------

export const HANDS = ['rock', 'paper', 'scissors'] as const;
export type Hand = (typeof HANDS)[number];

export function isHand(value: unknown): value is Hand {
  return HANDS.some((hand) => hand === value);
}

const BEATS: Record<Hand, Hand> = { rock: 'scissors', paper: 'rock', scissors: 'paper' };

export type Round = { host: Hand; guest: Hand };

/**
 * The rounds played so far, ties included, and the hands chosen in the round being played. A
 * chosen hand reaches nobody but its owner until the round is complete (`viewOf`).
 */
export type Rps = { kind: 'rps'; rounds: Round[]; host?: Hand; guest?: Hand; resigned?: Seat };

export function roundWinner(round: Round): Seat | null {
  if (round.host === round.guest) return null;
  return BEATS[round.host] === round.guest ? 'host' : 'guest';
}

export type Score = { host: number; guest: number };

export function scoreOf(rounds: readonly Round[]): Score {
  const score = { host: 0, guest: 0 };
  for (const round of rounds) {
    const winner = roundWinner(round);
    if (winner !== null) score[winner] += 1;
  }
  return score;
}

// ---------------------------------------------------------------------------------------
// Both games
// ---------------------------------------------------------------------------------------

export type DuelState = TicTacToe | Rps;

export function newDuel(kind: Duel): DuelState {
  return kind === 'tictactoe' ? { kind, board: EMPTY_BOARD } : { kind, rounds: [] };
}

/** How a game ended: who won — nobody, when the board filled up. `null` while it is on. */
export type Outcome = { winner: Seat | null; resigned: Seat | null } | null;

export function outcomeOf(state: DuelState): Outcome {
  if (state.resigned !== undefined) {
    return { winner: otherSeat(state.resigned), resigned: state.resigned };
  }
  if (state.kind === 'tictactoe') {
    const line = winningLine(state.board);
    if (line !== null) {
      return { winner: state.board[line[0]] === MARKS.guest ? 'guest' : 'host', resigned: null };
    }
    return state.board.includes('.') ? null : { winner: null, resigned: null };
  }
  const score = scoreOf(state.rounds);
  if (score.host >= RPS_WINS) return { winner: 'host', resigned: null };
  if (score.guest >= RPS_WINS) return { winner: 'guest', resigned: null };
  return null;
}

/**
 * Where a game stands. `expired` (an invitation nobody took) and `asleep` (a game nobody moved
 * in) are read off the clock rather than stored.
 */
export type Status = 'open' | 'running' | 'over' | 'expired' | 'asleep';

export type Clock = {
  now: number;
  /** When the invitation was posted. */
  createdAt: number;
  /** When somebody took it, or null while nobody has. */
  joinedAt: number | null;
  /** The last move, or the join before the first one. */
  movedAt: number;
  /** False once the guest's account has gone or been suspended: there is nobody left to move. */
  guestHere: boolean;
};

export function statusOf(state: DuelState, clock: Clock): Status {
  if (outcomeOf(state) !== null) return 'over';
  if (clock.joinedAt === null) {
    return clock.now - clock.createdAt >= INVITE_TTL_MS ? 'expired' : 'open';
  }
  if (!clock.guestHere) return 'asleep';
  return clock.now - clock.movedAt >= IDLE_TTL_MS ? 'asleep' : 'running';
}

export type Move = { square: number } | { hand: Hand } | { resign: true };

/** A move from a request body, or null if it is none the game knows. */
export function parseMove(kind: Duel, input: Record<string, unknown>): Move | null {
  if (input.resign === true) return { resign: true };
  if (kind === 'tictactoe') {
    const square = input.square;
    const onBoard = typeof square === 'number' && Number.isInteger(square);
    return onBoard && square >= 0 && square < EMPTY_BOARD.length ? { square } : null;
  }
  return isHand(input.hand) ? { hand: input.hand } : null;
}

export type Refusal = 'not_your_turn' | 'taken' | 'chosen' | 'invalid';

export type Played = { ok: true; state: DuelState } | { ok: false; reason: Refusal };

/**
 * A move by `seat`, by the rules alone. Whether the game is running at all needs the clock, and
 * is the caller's to check first.
 */
export function play(state: DuelState, seat: Seat, move: Move): Played {
  if ('resign' in move) return { ok: true, state: { ...state, resigned: seat } };

  if (state.kind === 'tictactoe') {
    if (!('square' in move)) return { ok: false, reason: 'invalid' };
    const { square } = move;
    if (!Number.isInteger(square) || square < 0 || square >= EMPTY_BOARD.length) {
      return { ok: false, reason: 'invalid' };
    }
    if (turnOf(state.board) !== seat) return { ok: false, reason: 'not_your_turn' };
    if (state.board[square] !== '.') return { ok: false, reason: 'taken' };
    const board = state.board.slice(0, square) + MARKS[seat] + state.board.slice(square + 1);
    return { ok: true, state: { ...state, board } };
  }

  if (!('hand' in move)) return { ok: false, reason: 'invalid' };
  if (state[seat] !== undefined) return { ok: false, reason: 'chosen' };
  const chosen: Rps =
    seat === 'host' ? { ...state, host: move.hand } : { ...state, guest: move.hand };
  const { host, guest } = chosen;
  if (host === undefined || guest === undefined) return { ok: true, state: chosen };
  // Both have chosen: the round is complete, and from now on everybody may see both hands.
  return { ok: true, state: { kind: 'rps', rounds: [...state.rounds, { host, guest }] } };
}

type Common = { status: Status; you: Seat | null; winner: Seat | null; resigned: Seat | null };

export type TicTacToeView = Common & {
  kind: 'tictactoe';
  board: string;
  /** Whose move it is, while the game runs. */
  turn: Seat | null;
  line: readonly number[] | null;
};

export type RpsView = Common & {
  kind: 'rps';
  rounds: Round[];
  score: Score;
  /** Who has chosen in the round being played: whether, never what. */
  chosen: { host: boolean; guest: boolean };
  /** Your own hand in that round, if you play and have chosen. */
  mine: Hand | null;
};

export type DuelView = TicTacToeView | RpsView;

/** A game as `you` may see it: as one of the players, or with `null` as anybody watching. */
export function viewOf(state: DuelState, status: Status, you: Seat | null): DuelView {
  const outcome = outcomeOf(state);
  const common = {
    status,
    you,
    winner: outcome?.winner ?? null,
    resigned: outcome?.resigned ?? null,
  };
  if (state.kind === 'tictactoe') {
    return {
      ...common,
      kind: 'tictactoe',
      board: state.board,
      turn: status === 'running' ? turnOf(state.board) : null,
      line: winningLine(state.board),
    };
  }
  return {
    ...common,
    kind: 'rps',
    rounds: state.rounds,
    score: scoreOf(state.rounds),
    chosen: { host: state.host !== undefined, guest: state.guest !== undefined },
    mine: you === null ? null : (state[you] ?? null),
  };
}

/**
 * A stored game read back, or null if it is not one. The rows are this server's own writes; the
 * check is for the day a game's shape changes, so that an old row draws as its message instead
 * of taking the whole chat down with it.
 */
export function readDuel(kind: Duel, value: unknown): DuelState | null {
  if (typeof value !== 'object' || value === null) return null;
  const state = value as Record<string, unknown>;
  if (state.kind !== kind) return null;
  if (state.resigned !== undefined && state.resigned !== 'host' && state.resigned !== 'guest') {
    return null;
  }
  if (kind === 'tictactoe') {
    return typeof state.board === 'string' && /^[.xo]{9}$/.test(state.board)
      ? (state as TicTacToe)
      : null;
  }
  const valid =
    Array.isArray(state.rounds) &&
    state.rounds.every(isRound) &&
    (state.host === undefined || isHand(state.host)) &&
    (state.guest === undefined || isHand(state.guest));
  return valid ? (state as Rps) : null;
}

function isRound(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) return false;
  const round = value as Record<string, unknown>;
  return isHand(round.host) && isHand(round.guest);
}

// ---------------------------------------------------------------------------------------
// Die magische Miesmuschel
// ---------------------------------------------------------------------------------------

/** What the Miesmuschel says. One answer is drawn on the server for every question. */
export const CONCH_ANSWERS = [
  'Ja.',
  'Nein.',
  'Vielleicht eines Tages.',
  'Ich glaube nicht.',
  'Frag noch einmal.',
  'Nichts.',
  'Keins von beiden.',
  'Auf jeden Fall.',
  'Eher nicht.',
  'Sieht gut aus.',
  'Jetzt nicht.',
  'Das weißt du doch selbst.',
] as const;

export type Conch = { kind: 'conch'; answer: string };

/** The answer for a draw in `0 … CONCH_ANSWERS.length - 1`, made by the caller. */
export function answerFor(draw: number): Conch {
  return { kind: 'conch', answer: CONCH_ANSWERS[draw] ?? CONCH_ANSWERS[0] };
}

export function readConch(value: unknown): Conch | null {
  if (typeof value !== 'object' || value === null) return null;
  const { kind, answer } = value as Record<string, unknown>;
  return kind === 'conch' && typeof answer === 'string' ? { kind, answer } : null;
}
